import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ANPublisher } from '../domain/dp-integration/an-publisher.model';
import { Observable } from 'rxjs';
import { ANSite } from '../domain/dp-integration/an-site.model';
import { HttpUtils } from '../../../shared/utils/http-utils';
import { DemandPartnerDbName } from '../domain/demand-partner.enum';
import { PMSite } from '../domain/dp-integration/pm-site.model';
import { PMAdSize } from '../domain/dp-integration/pm-ad-size.model';
import { PMIABCat } from '../domain/dp-integration/pm-iab-cat.model';
import { PmSiteRequest } from '../domain/dp-integration/pm-site-req.model';
import { PMAdUnit } from '../domain/dp-integration/pm-ad-unit.model';
import { DemandPartnerDetails } from '../../../shared/domain/demand-partner-details.model';
import { RubiconSite } from '../domain/dp-integration/rubicon-site.model';
import { RubiconAdSize } from '../domain/dp-integration/rubicon-ad-size.model';
import { RubiconCreateAdUnitRequest } from '../domain/dp-integration/rubicon-create-unit-request.model';
import { BridgeAdUnitDpHbId } from '../../../shared/domain/bridge-ad-unit-hb-id.model';

@Injectable()
export class DpConnectorsService {
  constructor(private http: HttpClient) {
  }

  rubiconSites(): Observable<RubiconSite[]> {
    return this.http.get<RubiconSite[]>(`${environment.serviceUrl}/dp-integration/rubicon/inventory/site`);
  }

  rubiconAdSizes(): Observable<RubiconAdSize[]> {
    return this.http.get<RubiconAdSize[]>(`${environment.serviceUrl}/dp-integration/rubicon/inventory/sizes`);
  }

  rubiconSiteCreate(site: RubiconSite) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/rubicon/inventory/site`, site);
  }

  rubiconAdUnitCreate(bridgeAdUnitId: string, req: RubiconCreateAdUnitRequest) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/rubicon/inventory/${bridgeAdUnitId}/ad-units`, req);
  }

  pubmaticSites(): Observable<PMSite[]> {
    return this.http.get<PMSite[]>(`${environment.serviceUrl}/dp-integration/pubmatic/inventory/site`);
  }

  pubmaticSiteCreate(siteReq: PmSiteRequest) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/pubmatic/inventory/site`, siteReq);
  }

  pubmaticUnitCreate(bridgeAdUnit: string, unit: PMAdUnit) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/pubmatic/inventory/${bridgeAdUnit}/ad-unit`, unit);
  }

  pubmaticSizes(): Observable<PMAdSize[]> {
    return this.http.get<PMAdSize[]>(`${environment.serviceUrl}/dp-integration/pubmatic/inventory/sizes`);
  }

  pubmaticIabCategories(): Observable<PMIABCat[]> {
    return this.http.get<PMIABCat[]>(`${environment.serviceUrl}/dp-integration/pubmatic/inventory/iabcats`);
  }

  appnexusPublisherPost(pub: ANPublisher) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/appnexus/inventory/publisher`, pub);
  }

  appnexusPublisherGet(): Observable<ANPublisher[]> {
    return this.http.get<ANPublisher[]>(`${environment.serviceUrl}/dp-integration/appnexus/inventory/publisher`);
  }

  appnexusSiteGet(pubId: string): Observable<ANSite[]> {
    return this.http.get<ANSite[]>(`${environment.serviceUrl}/dp-integration/appnexus/inventory/publisher/${pubId}/site`, {
      params: HttpUtils.getHttpParams({
        demandPartner: DemandPartnerDbName.APP_NEXUS
      })
    });
  }

  appnexusAdUnitPost(createReq: CreateAppNexusAdUnitRequest) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/appnexus/inventory/ad-unit`, createReq, {
      params: HttpUtils.getHttpParams({
        demandPartner: DemandPartnerDbName.APP_NEXUS
      })
    });
  }

  appnexusSitePost(siteReq: { publisherId?: string, name?: string }) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/appnexus/inventory/publisher/${siteReq.publisherId}/site`, siteReq);
  }

  dpDailyStatsImport(params: any): Observable<DemandPartnerDetails[]> {
    return this.http.get<DemandPartnerDetails[]>(`${environment.serviceUrl}/dp-integration/demand-partner/daily-stats-import?date=${params.date}&demandPartner=${params.demandPartner}`);
  }

  dpDailyStats(params: any): Observable<any> {
    return this.http.get<any>(`${environment.serviceUrl}/dp-integration/daily-stats`, {
      params: HttpUtils.getHttpParams({
        dateFrom: params.date,
        dateTo: params.date,
        demandPartner: params.demandPartner,
        page: params.pageNumber || 0,
        size: params.pageSize || 1000,

      })
    });
  }

  dpImport(params: any, dpDetails: DemandPartnerDetails[]) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/demand-partner/daily-stats-import?date=${params.date}&demandPartner=${params.demandPartner}`, dpDetails);
  }

  dpSync(params: any) {
    return this.http.post(`${environment.serviceUrl}/dp-integration/demand-partner/import-data?date=${params.date}&demandPartner=${params.demandPartner}`, null);
  }

  ebdaImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/ebda/upload-csv`, formData);
  }

  atosImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/atos/upload-xlsx`, formData);
  }

  vastImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/vast/upload-csv`, formData);
  }

  ebdaVideoImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/ebda/video-upload-csv`, formData);
  }

  videoUnitsImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/video/upload-csv`, formData);
  }

  emxImport(file: File): Observable<null> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<null>(`${environment.serviceUrl}/dp-integration/emx/upload-csv`, formData);
  }

  adUnitsCreateSpotxUnit(adUnitName: string): Observable<BridgeAdUnitDpHbId> {
    return this.http.post<BridgeAdUnitDpHbId>(`${environment.serviceUrl}/dp-integration/spotx/inventory/ad-units`, {
      name: adUnitName
    });
  }

}

export class CreateAppNexusAdUnitRequest {
  name: string;
  publisherId: string;
  siteId: number;
  width: any;
  height: any;
  floorPrice: number;
  _size: string;
}
